import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UserInfo, TokenPayload } from '@/types';

export interface AuthState {
  /** JWT persisted in sessionStorage — survives new tabs, clears on browser close */
  token: string | null;
  refreshToken: string | null;
  user: UserInfo | null;
  isAuthenticated: boolean;

  /** Set auth state after successful login. */
  setAuth: (token: string, refreshToken: string) => void;

  /** Update token after refresh. */
  updateToken: (token: string) => void;

  /** Clear all auth state on logout. */
  logout: () => void;
}

/**
 * Decode the payload section of a JWT without external libraries.
 * Does NOT verify the signature — that is the server's responsibility.
 *
 * JWTs use base64url encoding (RFC 7515 §2): `-` replaces `+`, `_` replaces `/`,
 * and trailing `=` padding is stripped. Browser `atob` only accepts standard
 * base64, so we MUST translate before decoding. A previous version skipped
 * this and threw `InvalidCharacterError` whenever the payload happened to
 * contain a `-` or `_`, which surfaced to the LoginPage as a generic
 * "Invalid credentials" — masking what was actually a client-side parse bug.
 */
export function decodeJwtPayload(token: string): TokenPayload {
  const payloadBase64Url = token.split('.')[1];
  const payloadBase64 = base64UrlToBase64(payloadBase64Url);
  const payloadJson = atob(payloadBase64);
  return JSON.parse(payloadJson) as TokenPayload;
}

/**
 * Converts a base64url-encoded string to standard base64 by reversing the
 * URL-safe character substitutions and re-adding `=` padding to the next
 * multiple of 4. Pure / exported so unit tests can pin the policy.
 */
export function base64UrlToBase64(input: string): string {
  const replaced = input.replace(/-/g, '+').replace(/_/g, '/');
  const paddingNeeded = (4 - (replaced.length % 4)) % 4;
  return replaced + '='.repeat(paddingNeeded);
}

/**
 * Microsoft-schema role claim URI emitted by ASP.NET Core when a Claim of
 * type <c>ClaimTypes.Role</c> is added to a JWT (see TokenService.cs in the
 * IdentityAuthService project). The Identity Service wires roles as
 * <c>ClaimTypes.Role</c>, so the JWT carries them under this URI — NOT under
 * a plain <c>"role"</c> key. Reading <c>payload.role</c> silently produced
 * <c>undefined</c> and broke role-gated UI later.
 */
const MS_ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

/**
 * Extract UserInfo from a decoded JWT payload. Resilient to:
 *  - roles emitted under the Microsoft schema URI (single string OR array),
 *  - missing <c>displayName</c> claim — fall back to email so the UI has
 *    something to render in the agent header.
 */
export function extractUserInfo(payload: TokenPayload): UserInfo {
  return {
    agentId: payload.sub,
    tenantId: payload.tenantId,
    tenantName: payload.tenantName,
    email: payload.email,
    displayName: payload.displayName ?? payload.email ?? '',
    role: extractRole(payload),
  };
}

/**
 * Pulls the first role from the Microsoft schema-URI claim, falling back
 * to the legacy <c>role</c> field if the token format ever changes.
 * Pure / exported for testability — pins the role-claim contract with the
 * Identity Service.
 */
export function extractRole(payload: TokenPayload): string {
  // The runtime shape of the JWT may include the schema-URI claim that's
  // not on the TokenPayload type. Reach via index access without losing
  // type safety on the rest of the payload.
  const raw = (payload as unknown as Record<string, unknown>)[MS_ROLE_CLAIM]
    ?? (payload as unknown as Record<string, unknown>)['role']
    ?? payload.role;
  if (Array.isArray(raw)) {
    return raw.length > 0 ? String(raw[0]) : '';
  }
  return raw == null ? '' : String(raw);
}

/**
 * EVERY role on the token, not just the first. `extractRole` keeps only the first one for the
 * header's display label; a permission check needs the whole set, because roles are additive
 * (a user holding `developer` first and `agent` second still holds `workspace.handle`).
 * Same claim lookup order as `extractRole`. An absent / empty claim yields `[]` (no roles, so no
 * permissions).
 */
export function extractRoles(payload: TokenPayload): string[] {
  const raw = (payload as unknown as Record<string, unknown>)[MS_ROLE_CLAIM]
    ?? (payload as unknown as Record<string, unknown>)['role']
    ?? payload.role;
  if (Array.isArray(raw)) {
    return raw.filter((r) => r != null && String(r).trim() !== '').map((r) => String(r));
  }
  return raw == null || String(raw).trim() === '' ? [] : [String(raw)];
}

/**
 * Returns true iff the JWT's <c>exp</c> claim is in the future.
 * Used on hydrate to decide whether a persisted token still lets the agent
 * stay on the dashboard or must go through /login again.
 */
function isTokenStillValid(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const payload = decodeJwtPayload(token);
    // exp is seconds-since-epoch per JWT spec
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      setAuth: (token: string, refreshToken: string) => {
        const payload = decodeJwtPayload(token);
        const user = extractUserInfo(payload);
        set({
          token,
          refreshToken,
          user,
          isAuthenticated: true,
        });
      },

      updateToken: (token: string) => {
        const payload = decodeJwtPayload(token);
        const user = extractUserInfo(payload);
        set((state) => ({
          token,
          // OP-367 — the refreshed token doesn't re-carry the tenantName claim, and the tenant
          // name is stable for a session; keep the login-time value rather than dropping to the GUID.
          user: { ...user, tenantName: user.tenantName ?? state.user?.tenantName },
        }));
      },

      logout: () => {
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'css-agent-auth',
      storage: createJSONStorage(() => localStorage),
      // IMPORTANT: do NOT persist `isAuthenticated` directly. It used to be stored
      // in localStorage, which meant a stale token from days ago was enough to skip
      // the /login redirect — the dashboard loaded, every API call 401'd, and the
      // agent never saw a login form. Instead, only persist the credentials and
      // derive `isAuthenticated` from the token's exp claim on hydrate.
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const valid = isTokenStillValid(state.token);
        state.isAuthenticated = valid;
        if (!valid) {
          // Token already expired — scrub the useless credentials so interceptors
          // don't send them.
          state.token = null;
          state.refreshToken = null;
          state.user = null;
        }
      },
    }
  )
);
